import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { SystemDashboardSearch } from '../types/dashboard.types';
import {
  toReportingPeriodFilterSearch,
  toReportingPeriodFilterValues,
} from '../utils/reportingPeriodFilters';

const ROUTES = {
  system: getRouteApi('/_auth/dashboards/system/'),
  der: getRouteApi('/_auth/dashboards/der'),
} as const;

export type ReportingPeriodRouteId = keyof typeof ROUTES;

const normalizeFilterValues = (values: Record<string, string>) =>
  toReportingPeriodFilterValues(toReportingPeriodFilterSearch(values));

export function useReportingPeriodFilters(routeId: ReportingPeriodRouteId) {
  const route = ROUTES[routeId];
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toReportingPeriodFilterValues(search), [search]);
  const defaultValues = useMemo(() => getMonthToDateRange(), []);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toReportingPeriodFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const params = useMemo(
    (): SystemDashboardSearch & { dateFrom: string; dateTo: string } => ({
      ...search,
      dateFrom: search.dateFrom ?? defaultValues.dateFrom,
      dateTo: search.dateTo ?? defaultValues.dateTo,
    }),
    [search, defaultValues],
  );

  return { ...filters, params };
}
